import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
import posManagementService from "../../services/agendaApi/posManagement.service";
import { Workbook } from "exceljs";
import { IPosBulkError, IPosBulkUploadResult } from "../../models/input/agendaApi/IPosMassiveIn";
import {
  IParsedHeader,
  IValidatedPosRow,
  MAX_POS_RECORDS,
  parsePosWorksheet,
  resolveColumnIndexes,
  validatePosRows,
} from "../../utils/posBulkUploadUtils";
import { triggerBlobDownload } from "../../utils/downloadFile";

export type PosUploadPhase = "idle" | "parsing" | "preview" | "uploading" | "result" | "failure";

// Prevents Excel/CSV formula injection: a leading =, +, -, @, tab, or CR would otherwise execute as a formula when the exported file is opened.
const sanitizeForSpreadsheet = (value: unknown): unknown =>
  typeof value === "string" && /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;

export type { IParsedHeader, IValidatedPosRow };

export const usePosBulkUpload = () => {
  const [t] = useTranslation("global");
  const languageID: any = useSelector((state: RootState) => state.languageId);

  const [openModalCreateDone, setOpenModalCreateDone] = useState(false);
  const [openModalError, setOpenModalError] = useState(false);

  const [loading, setLoading] = useState<boolean>(false);
  const [loadingModal, setLoadingModal] = useState<boolean>(false);
  const [fileName, setFileName] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [errorFilePicker, setErrorFilePicker] = useState(false);
  const [openModalCreate, setOpenModalCreate] = useState(false);
  const [openModalHelp, setOpenModalHelp] = useState(false);

  const [phase, setPhase] = useState<PosUploadPhase>("idle");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [parsedHeaders, setParsedHeaders] = useState<IParsedHeader[]>([]);
  const [validatedRows, setValidatedRows] = useState<IValidatedPosRow[]>([]);
  const [bulkResult, setBulkResult] = useState<IPosBulkUploadResult | null>(null);

  const showPreviewTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => {
      if (showPreviewTimeoutRef.current) {
        clearTimeout(showPreviewTimeoutRef.current);
      }
    };
  }, []);

  const resetPreviewState = () => {
    setPhase("idle");
    setSelectedFile(null);
    setParsedHeaders([]);
    setValidatedRows([]);
    setFileName("");
    setErrorFilePicker(false);
    setErrorMessage("");
    setOpenModalCreateDone(false);
  };

  const resetResultState = () => {
    setBulkResult(null);
    resetPreviewState();
  };

  const confirmDisabled = useMemo(
    () => phase !== "preview" || validatedRows.every((row) => row._status !== "valid"),
    [phase, validatedRows]
  );

  const handleDownloadTemplate = () => {
    setLoading(true);
    posManagementService
      .getPosBulkUploadTemplate(Number(languageID.value))
      .then((r: Blob | { statusCode: number }) => {
        try {
          if ((r as { statusCode: number })?.statusCode === 401) {
            setOpenModalError(true);
            console.error("Error:", "Download failed");
          } else if (r instanceof Blob) {
            try {
              triggerBlobDownload(r, "template_create_pos_bulk.xlsx");
            } catch (error) {
              setOpenModalError(true);
              console.error("Error:", error);
            }
          }
        } catch (error) {
          setOpenModalError(true);
          console.error("Error:", error);
        } finally {
          setLoading(false);
        }
      });
  };

  const handleCreatePos = () => {
    setOpenModalCreate(true);
    resetPreviewState();
  };

  const handleOnClickExit = () => {
    setOpenModalCreate(false);
    resetPreviewState();
  };

  // Closes the popup while keeping the parsed preview data intact, so the
  // preview renders inline on the page instead of inside the modal.
  const handleShowPreview = () => {
    setLoadingModal(true);
    setOpenModalCreate(false);
    showPreviewTimeoutRef.current = setTimeout(() => setLoadingModal(false), 400);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileInput = e.target;
    if (!fileInput.files) {
      setErrorMessage(t("modal.errorLoadingFile"));
      setErrorFilePicker(true);
      setPhase("failure");
      return;
    }

    const file = fileInput.files[0];
    setFileName(file.name);
    setSelectedFile(file);
    const fileType = file.name.split(".").pop();

    if (fileType?.toLowerCase() !== "xlsx") {
      setErrorMessage(t("header.messageXLSX"));
      setErrorFilePicker(true);
      setPhase("failure");
      return;
    }

    setErrorFilePicker(false);
    setErrorMessage("");
    setPhase("parsing");
    setLoadingModal(true);

    let data: ArrayBuffer;
    try {
      data = await file.arrayBuffer();
    } catch (error) {
      console.error(error);
      setErrorFilePicker(true);
      setErrorMessage(t("modal.errorLoadingFile"));
      setPhase("failure");
      setLoadingModal(false);
      return;
    }

    const workbook = new Workbook();

    try {
      await workbook.xlsx.load(data);
    } catch (error) {
      console.error(error);
      setErrorFilePicker(true);
      setErrorMessage(t("modal.errorLoadingFile"));
      setPhase("failure");
      setLoadingModal(false);
      return;
    }

    let headers: IParsedHeader[];
    let rows: Record<string, any>[];
    try {
      const parsed = parsePosWorksheet(workbook.worksheets[0]);
      headers = parsed.headers;
      rows = parsed.rows;
    } catch (error) {
      console.error(error);
      setErrorFilePicker(true);
      setErrorMessage(t("modal.errorLoadingFile"));
      setPhase("failure");
      setLoadingModal(false);
      return;
    }

    if (rows.length > MAX_POS_RECORDS) {
      setErrorFilePicker(true);
      setErrorMessage(t("modal.posBulkUploadExceedsLimit"));
      setPhase("failure");
      setLoadingModal(false);
      return;
    }

    const columnIndex = resolveColumnIndexes(headers);
    const validated = validatePosRows(rows, t, columnIndex);

    setParsedHeaders(headers);
    setValidatedRows(validated);
    setPhase("preview");
    setLoadingModal(false);
  };

  const onClearFilePicker = () => {
    resetPreviewState();
  };

  const submitPos = async () => {
    if (phase === "uploading" || !selectedFile) return;

    setPhase("uploading");
    setLoadingModal(true);

    // Backend contract (confirmed via Swagger): Pos/CreatePOSBulk takes the raw .xlsx as
    // multipart/form-data (field "file") + LanguageID query param, not a JSON payload.
    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const response = await posManagementService.createPOSBulk(formData, Number(languageID.value));

      // postApi never rejects; on any failure it resolves a non-object (e.g. {statusCode:401}).
      if (!response || typeof response.totalProcessed !== "number") {
        setPhase("failure");
        setOpenModalCreate(false);
        setOpenModalError(true);
        setLoadingModal(false);
        return;
      }

      setBulkResult(response);
      setPhase("result");
      setOpenModalCreate(false);
      setOpenModalCreateDone(true);
      setLoadingModal(false);
    } catch (error) {
      console.error(error);
      setPhase("failure");
      setOpenModalCreate(false);
      setOpenModalError(true);
      setLoadingModal(false);
    }
  };

  // On-demand export of the API's rejected-row errors, triggered from the results section's
  // "Download Error Report" button.
  const handleDownloadErrorReport = async (errors: IPosBulkError[]) => {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("Datos");

    worksheet.columns = [
      { header: t("header.rowNumber"), key: "rowNumber", width: 15 },
      { header: t("header.errorDescription"), key: "description", width: 40 },
    ];

    errors.forEach((item) => {
      const row = worksheet.addRow({ ...item, description: sanitizeForSpreadsheet(item.description) });
      row.eachCell((cell) => {
        cell.alignment = { horizontal: "center" };
      });
    });

    const headerRow = worksheet.getRow(1);
    headerRow.eachCell((cell) => {
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "00B0F0" } };
      cell.alignment = { horizontal: "center" };
      cell.border = {
        top: { style: "thin", color: { argb: "000000" } },
        left: { style: "thin", color: { argb: "000000" } },
        bottom: { style: "thin", color: { argb: "000000" } },
        right: { style: "thin", color: { argb: "000000" } },
      };
      cell.font = { bold: true };
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/octet-stream" });
    triggerBlobDownload(blob, "pos_bulk_upload_errors.xlsx");
  };

  return {
    handleCreatePos,
    setOpenModalHelp,
    openModalHelp,
    loading,
    handleDownloadTemplate,
    loadingModal,
    openModalCreate,
    handleOnClickExit,
    handleShowPreview,
    fileName,
    errorFilePicker,
    errorMessage,
    handleFileChange,
    submitPos,
    onClearFilePicker,
    openModalCreateDone,
    openModalError,
    setOpenModalCreateDone,
    setOpenModalError,
    phase,
    selectedFile,
    parsedHeaders,
    validatedRows,
    confirmDisabled,
    bulkResult,
    resetResultState,
    handleDownloadErrorReport,
  };
};
